import { Button, DecimalInput, Dialog, Label, MoneyInput, TextField } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

/**
 * Add a stock, or correct its name and symbol.
 *
 * THE SYMBOL FIELD SAYS WHAT IT IS FOR. It is the identifier the price vendor recognises, not a
 * free-text label: `ASML.AS` and `ASML` are different things to Finnhub, and a household that enters
 * the second will get a stock 011 fails to price every day forever. The hint is the cheapest place to
 * prevent that.
 *
 * CORRECTING IT IS A FIRST-CLASS OPERATION for the same reason — this form serves both, so the fix
 * is exactly as easy as the mistake.
 *
 * ==========================================================================================
 * ADDING A STOCK OFFERS THE FIRST PURCHASE, BECAUSE NOBODY ADDS A STOCK THEY DO NOT OWN
 * ==========================================================================================
 *
 * A stock on its own is an identity with no holding: it charts nothing, it is worth nothing, and the
 * positions table renders it as a row of em-dashes. That is a legitimate state — a household may add
 * the symbol first and record the trade from the contract note later — but it is not what someone
 * doing this for the first time expects, and the original form gave them no way to say otherwise
 * without finding the stock detail view afterwards.
 *
 * So the purchase fields are here, and they are OPTIONAL AS A GROUP: leave them empty and you get the
 * bare stock exactly as before; fill in shares and you must also give a price, because a purchase
 * missing half of itself is not a record of anything. `firstBuy` is null in the first case.
 *
 * It stays two calls rather than one — the endpoint takes a stock, and transactions hang off the
 * stock it returns. If the second fails, the stock exists with no transactions, which is the
 * legitimate state above rather than a broken one, and the household can record the buy from the
 * detail view. Worth knowing before this is "fixed" into one atomic endpoint.
 */

const today = (): string => new Date().toISOString().slice(0, 10);

export interface StockFormSubmit {
  name: string;
  symbol: string;
  exchange: string | null;
  /** The opening purchase, when one was entered. Null leaves the stock with no transactions. */
  firstBuy: { shares: string; pricePerShare: string; fee: string; date: string } | null;
}

export interface StockFormProps {
  open: boolean;
  onClose: () => void;
  /** Null when adding; the current values when correcting. */
  editing: { name: string; symbol: string; exchange: string | null } | null;
  onSubmit: (input: StockFormSubmit) => void;
  pending: boolean;
  error?: string | null;
}

export function StockForm({
  open,
  onClose,
  editing,
  onSubmit,
  pending,
  error,
}: StockFormProps): ReactNode {
  const t = useT();
  const [name, setName] = useState(editing?.name ?? "");
  const [symbol, setSymbol] = useState(editing?.symbol ?? "");
  const [exchange, setExchange] = useState(editing?.exchange ?? "");
  const [shares, setShares] = useState("");
  const [price, setPrice] = useState("");
  const [fee, setFee] = useState("");
  const [date, setDate] = useState(today());

  // Either field started means the household is recording a purchase, so the other becomes required.
  // Checked on the values rather than on a "do you want to add one" toggle, because the toggle would
  // be a click that says nothing the fields do not already say.
  const startedBuy = shares.trim() !== "" || price.trim() !== "";

  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit({
      name,
      symbol,
      exchange: exchange.trim() === "" ? null : exchange.trim(),
      firstBuy:
        editing === null && startedBuy
          ? { shares, pricePerShare: price, fee: fee.trim() === "" ? "0" : fee, date }
          : null,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t("inv.form.editHeading") : t("inv.page.addStock")}
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="stock-name">{t("inv.form.name")}</Label>
          <TextField
            id="stock-name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder="ASML Holding NV"
            required
          />
        </div>

        <div>
          <Label htmlFor="stock-symbol">{t("inv.form.ticker")}</Label>
          <TextField
            id="stock-symbol"
            value={symbol}
            onChange={(event) => {
              setSymbol(event.target.value);
            }}
            placeholder="ASML.AS"
            required
          />
          <p className="text-muted mt-1 text-xs">
            {t("inv.form.tickerHint")}
            <code>AAPL</code> for US listings, <code>ASML.AS</code> for Euronext Amsterdam,{" "}
            <code>BMW.DE</code> for XETRA.
          </p>
        </div>

        <div>
          <Label htmlFor="stock-exchange">{t("inv.form.exchange")}</Label>
          <TextField
            id="stock-exchange"
            value={exchange}
            onChange={(event) => {
              setExchange(event.target.value);
            }}
            placeholder="Euronext Amsterdam"
          />
        </div>

        {/* Editing a stock never touches its transactions — those have their own form, their own
            history and their own rules about what a later sell still covers. */}
        {editing === null && (
          <fieldset className="rounded-control border border-line p-4">
            <legend className="px-1 text-sm font-medium">{t("inv.form.whatYouBought")}</legend>
            <p className="text-muted mb-3 text-xs">{t("inv.form.buyLater")}</p>

            <div className="flex flex-col gap-4">
              <div>
                <Label htmlFor="stock-shares">{t("inv.table.shares")}</Label>
                <DecimalInput
                  id="stock-shares"
                  scale={4}
                  value={shares}
                  onValueChange={(next) => {
                    setShares(next);
                  }}
                  placeholder="10"
                  required={startedBuy}
                />
                <p className="text-muted mt-1 text-xs">{t("inv.form.sharesHint")}</p>
              </div>

              <div>
                <Label htmlFor="stock-price">{t("inv.price.heading")}</Label>
                <DecimalInput
                  id="stock-price"
                  scale={4}
                  value={price}
                  onValueChange={(next) => {
                    setPrice(next);
                  }}
                  placeholder="600.00"
                  required={startedBuy}
                />
              </div>

              <div>
                <Label htmlFor="stock-fee">{t("inv.form.fee")}</Label>
                <MoneyInput
                  id="stock-fee"
                  value={fee}
                  onValueChange={(next) => {
                    setFee(next);
                  }}
                  placeholder="2.50"
                />
              </div>

              <div>
                {/* Capped at today for the same reason `TransactionForm` caps it: the portfolio
                    records what has happened. The server refuses a future date regardless, and its
                    refusal is the one that carries the reason. */}
                <Label htmlFor="stock-date">{t("inv.form.purchaseDate")}</Label>
                <TextField
                  id="stock-date"
                  type="date"
                  value={date}
                  max={today()}
                  onChange={(event) => {
                    setDate(event.target.value);
                  }}
                  required={startedBuy}
                />
              </div>
            </div>
          </fieldset>
        )}

        {error && <p className="text-danger text-sm">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={pending}>
            {editing ? t("common.save") : t("inv.form.addStock")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
